import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Howl } from 'howler';
import * as PrismicDOM from 'prismic-dom';
import { default as Prismic } from 'prismic-javascript';
import { getGamificationProfileAction } from '../actions/gamification.actions';
import { GamificationService } from '../gamification.service';
import { OeAppPersonService } from '../oe-app-person.service';
import { OeAppService } from '../oe-app.service';
import { AppState } from '../reducers/index.tx';
import { TranslationService } from '../translation.service';
import { ActivityNotification } from '../types/activity-notification';
import { GamificationPersonBadgeDto } from '../types/GamificationPersonBadgeDto';
import { ProgressBadgeDto } from '../types/ProgressResultDto';
import { ActivityModalNotificationComponent } from './activity-modal-notification/activity-modal-notification.component';

@Injectable({
  providedIn: 'root',
})
export class ActivityNotificationService {
  localStorageKey = 'gamification-notification';

  notificationTimeout = 120000; //2 minutes

  subtleNotification: ActivityNotification;

  tmrSubtle;

  contactId = this.oeAppPersonService.getPersonData().contactId;

  currentLanguage: string;

  restrictedPages = ['login.html', 'token/passwordSetup.html', 'migration/passwordSetup.html'];

  constructor(
    private modalService: NgbModal,
    private store: Store<AppState>,
    private oeApp: OeAppService,
    private oeAppPersonService: OeAppPersonService,
    private gamificationService: GamificationService,
    private translationService: TranslationService
  ) {
    this.checkCachedNotification();

    window.addEventListener('person-progress-saved', (event: CustomEvent) => {
      this.activityCompleted(
        {
          points: event.detail.awardedPoints,
          badges: event.detail.awardedBadges,
          overTheLimitActivity: event.detail.overTheLimitActivity,
        },
        true,
        !!event.detail.skipSubtle
      );
    });

    // Listen to iframe event
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.data && typeof event.data === 'object') {
        this.activityCompleted(
          {
            points: event.data.awardedPoints,
            badges: event.data.awardedBadges,
            overTheLimitActivity: event.data.overTheLimitActivity,
          },
          true,
          !!event.data.skipSubtle
        );
      }
    });
  }

  activityCompleted(data: ActivityNotification, cache: boolean, skipSubtle: boolean) {
    if ((data.points || (data.badges && data.badges.length) || data.overTheLimitActivity)) {
      // test sound effect
      let sound = new Howl({
        src: ['/images/gamification/notification.mp3'],
      });

      sound.play();

      if (skipSubtle) {
        cache = false;
        this.subtleNotification = { ...data, hidden: true };
        this.showModalNotification();
      } else {
        this.showSubtleNotification(data);
      }

      // Refresh gamification data
      this.store.dispatch(getGamificationProfileAction());

      if (cache) {
        // Cache the notification
        localStorage.setItem(
          this.localStorageKey,
          JSON.stringify({
            points: data.points,
            badges: data.badges,
            overTheLimitActivity: data.overTheLimitActivity,
            timestamp: Date.now(),
          })
        );
      }
    }
  }

  isRestricted(url: string) {
    return this.restrictedPages.some((page: string) => url.indexOf(page) !== -1);
  }

  showSubtleNotification(data: ActivityNotification) {
    // Don't show the subtle notification in restricted pages
    if (this.isRestricted(location.href)) {
      return;
    }

    this.subtleNotification = data;

    if (this.tmrSubtle) {
      clearTimeout(this.tmrSubtle);
    }

    this.tmrSubtle = setTimeout(() => this.removeSubtleNotification(true), this.notificationTimeout);
  }

  removeSubtleNotification(removeStorage: boolean) {
    this.subtleNotification = undefined;

    if (removeStorage) {
      localStorage.removeItem(this.localStorageKey);
    }
  }

  showModalNotification() {
    if (this.subtleNotification.badges) {
      this.gamificationService
        .getPersonBadges(
          this.contactId,
          this.subtleNotification.badges.map((e: ProgressBadgeDto) => e.uuid)
        )
        .subscribe((data: GamificationPersonBadgeDto[]) => {
          if (data.length) {
            // Set the badge activity type for the CMS interrogation
            this.subtleNotification.badges.forEach((badge: ProgressBadgeDto) => {
              const dataActivity = data.find((e) => e.activityBadge.uuid === badge.uuid);
              if (dataActivity && dataActivity.activityBadge) {
                badge.activityType = dataActivity.activityBadge.activityType;
              }
            });

            if (!this.currentLanguage) {
              this.currentLanguage = this.translationService.getCurrentLanguage();
            }

            // Get badges data from CMS
            Prismic.getApi(this.oeApp.prismicUrl())
              .then((api) => {
                return api.query(
                  [
                    Prismic.Predicates.any(
                      'my.activity-type-reason.type',
                      this.subtleNotification.badges.map((e: ProgressBadgeDto) => e.activityType)
                    ),
                  ],
                  { lang: `${this.currentLanguage}-oe` }
                );
              })
              .then((response) => {
                if (response.results && response.results.length) {
                  this.subtleNotification.badges.forEach((badge: ProgressBadgeDto) => {
                    const badgeContent = response.results.find((e) => e.data.type === badge.activityType);
                    if (badgeContent) {
                      badge.description = PrismicDOM.RichText.asHtml(badgeContent.data.message);
                    }
                  });
                }

                this.showModalNotificationFinal();
              });
          } else {
            this.showModalNotificationFinal();
          }
        });
    } else {
      this.showModalNotificationFinal();
    }
  }

  showModalNotificationFinal() {
    if (!this.modalService.hasOpenModals()) {
      const modalRef = this.modalService.open(ActivityModalNotificationComponent, { size: 'lg' });
      modalRef.componentInstance.notification = {
        ...this.subtleNotification,
      };

      this.removeSubtleNotification(true);
    }
  }

  checkCachedNotification() {
    const notificationString = localStorage.getItem(this.localStorageKey);
    if (notificationString) {
      const notification = JSON.parse(notificationString);

      if (Date.now() - notification.timestamp > this.notificationTimeout) {
        localStorage.removeItem(this.localStorageKey);
      } else {
        this.showSubtleNotification({
          points: notification.points,
          badges: notification.badges,
          overTheLimitActivity: notification.overTheLimitActivity,
        });
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/activity-notification/activity-notification.service.ts